import { BANKING_REGIONS, BankingRegion, regionForIso } from './banking-regions';

export { BANKING_REGIONS, type BankingRegion, regionForIso };

export function digitsOnly(value: string): string {
  return (value ?? '').replace(/[^0-9]/g, '');
}

export function normalizeIban(value: string): string {
  return (value ?? '').replace(/\s+/g, '').toUpperCase();
}

/** Display grouping: DE89 3704 0044 0532 0130 00 */
export function formatIbanDisplay(value: string): string {
  return normalizeIban(value).replace(/(.{4})/g, '$1 ').trim();
}

/** Display as 12-34-56; the model value stays digits-only. */
export function formatSortCodeDisplay(value: string): string {
  const digits = digitsOnly(value).slice(0, 6);
  return digits.replace(/(\d{2})(?=\d)/g, '$1-');
}

/** •••• 5678 for masked account / IBAN display. */
export function maskAccountTail(value: string | undefined | null, visible = 4): string {
  const v = (value ?? '').replace(/\s+/g, '');
  if (!v) return '';
  return `•••• ${v.slice(-visible)}`;
}

// ---------------------------------------------------------------------------
// IBAN validation (mirrors UniCreate-ligher-API bank-format.validation.ts)
// ---------------------------------------------------------------------------

const IBAN_LENGTHS: Record<string, number> = {
  AT: 20,
  BE: 16,
  CY: 28,
  DE: 22,
  EE: 20,
  ES: 24,
  FI: 18,
  FR: 27,
  GR: 27,
  HR: 21,
  IE: 22,
  IT: 27,
  LT: 20,
  LU: 20,
  LV: 21,
  MT: 31,
  NL: 18,
  PT: 25,
  SI: 19,
  SK: 24,
};

function ibanChecksumIsValid(iban: string): boolean {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const value = char >= '0' && char <= '9' ? char : String(char.charCodeAt(0) - 55);
    remainder = Number(`${remainder}${value}`) % 97;
  }
  return remainder === 1;
}

export function ibanError(value: string, expectedCountry?: string): string | null {
  const iban = normalizeIban(value);
  if (!iban) return 'IBAN is required';
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(iban)) {
    return 'An IBAN starts with a 2-letter country code followed by digits';
  }
  const country = iban.slice(0, 2);
  if (expectedCountry && country !== expectedCountry.toUpperCase()) {
    const region = regionForIso(country);
    return `This IBAN is for ${region?.label ?? country}, not the country you selected`;
  }
  const expectedLength = IBAN_LENGTHS[country];
  if (expectedLength && iban.length !== expectedLength) {
    return `A ${country} IBAN is ${expectedLength} characters (you entered ${iban.length})`;
  }
  if (iban.length < 15 || iban.length > 34) {
    return 'This IBAN has the wrong length';
  }
  if (!ibanChecksumIsValid(iban)) {
    return 'This IBAN failed its check digits — please re-check it';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Corridor-specific validators (GB / US)
// ---------------------------------------------------------------------------

export function ukSortCodeError(value: string): string | null {
  const digits = digitsOnly(value);
  if (!digits) return 'Sort code is required';
  if (digits.length !== 6) return 'Sort code must be 6 digits';
  return null;
}

export function ukAccountNumberError(value: string): string | null {
  const digits = digitsOnly(value);
  if (!digits) return 'Account number is required';
  if (digits.length !== 8) return 'UK account numbers are 8 digits';
  return null;
}

export function abaRoutingError(value: string): string | null {
  const digits = digitsOnly(value);
  if (!digits) return 'Routing number is required';
  if (digits.length !== 9) return 'Routing numbers are 9 digits';
  const d = digits.split('').map(Number);
  const checksum = 3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8]);
  if (checksum === 0 || checksum % 10 !== 0) {
    return 'This routing number failed its check digit — please re-check it';
  }
  return null;
}

export function usAccountNumberError(value: string): string | null {
  const digits = digitsOnly(value);
  if (!digits) return 'Account number is required';
  if (digits.length < 4 || digits.length > 17) return 'US account numbers are 4 to 17 digits';
  return null;
}

// ---------------------------------------------------------------------------
// Generic field helpers
// ---------------------------------------------------------------------------

export function accountNameError(value: string, entityType: 'PERSONAL' | 'COMPANY'): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return entityType === 'COMPANY' ? 'Company name is required' : 'Account holder name is required';
  }
  if (trimmed.length < 2) return 'Must be at least 2 characters';
  return null;
}

export function requiredError(value: string, label: string): string | null {
  return (value ?? '').trim() ? null : `${label} is required`;
}

export function usZipError(value: string): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return 'ZIP code is required';
  if (!/^\d{5}(-\d{4})?$/.test(trimmed)) return 'Enter a 5-digit ZIP code (or ZIP+4)';
  return null;
}

